import { Button, Group, Modal, Stack, Switch, Text, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { AxiosError } from 'axios';
import { useCreateTraeger, useUpdateTraeger } from '@/api/useTraeger';
import type { Traeger } from '@/api/types';

interface TraegerEditDialogProps {
  opened: boolean;
  traeger: Traeger | null;
  onClose: () => void;
}

interface FormValues {
  name: string;
  finanzservice_intern: boolean;
}

function describeError(err: unknown): string {
  const ax = err as AxiosError<{ detail?: string | { message?: string } }> | undefined;
  const detail = ax?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (detail && typeof detail === 'object' && 'message' in detail && detail.message) {
    return String(detail.message);
  }
  return ax?.message ?? '';
}

export function TraegerEditDialog({ opened, traeger, onClose }: TraegerEditDialogProps) {
  const { t } = useTranslation(['traeger', 'common', 'errors']);
  const create = useCreateTraeger();
  const update = useUpdateTraeger();
  const isEdit = traeger !== null;

  const form = useForm<FormValues>({
    initialValues: {
      name: traeger?.name ?? '',
      finanzservice_intern: traeger?.finanzservice_intern ?? false,
    },
    validate: {
      name: (v) => (v.trim().length > 0 ? null : t('traeger:validation.nameRequired')),
    },
  });

  useEffect(() => {
    if (opened) {
      form.setValues({
        name: traeger?.name ?? '',
        finanzservice_intern: traeger?.finanzservice_intern ?? false,
      });
      form.resetDirty();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, traeger]);

  const submit = form.onSubmit(async (values) => {
    try {
      if (isEdit && traeger) {
        await update.mutateAsync({
          id: traeger.id,
          patch: {
            name: values.name,
            finanzservice_intern: values.finanzservice_intern,
          },
        });
        notifications.show({ color: 'teal', message: t('traeger:info.updated') });
      } else {
        await create.mutateAsync({
          name: values.name,
          finanzservice_intern: values.finanzservice_intern,
        });
        notifications.show({ color: 'teal', message: t('traeger:info.created') });
      }
      onClose();
    } catch (err) {
      notifications.show({
        color: 'red',
        title: t('errors:generic'),
        message: describeError(err) || t('errors:generic'),
      });
    }
  });

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={isEdit ? t('traeger:form.edit.title') : t('traeger:form.create.title')}
      centered
    >
      <form onSubmit={submit}>
        <Stack>
          <TextInput
            label={t('traeger:form.field.name')}
            placeholder={t('traeger:form.field.namePlaceholder')}
            required
            {...form.getInputProps('name')}
          />
          <Switch
            label={t('traeger:form.field.finanzservice')}
            {...form.getInputProps('finanzservice_intern', { type: 'checkbox' })}
          />
          <Text size="xs" c="dimmed">
            {t('traeger:form.field.finanzserviceHint')}
          </Text>
          <Group justify="flex-end" mt="sm">
            <Button variant="default" onClick={onClose}>
              {t('common:actions.cancel')}
            </Button>
            <Button type="submit" loading={create.isPending || update.isPending}>
              {t('common:actions.save')}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
